import { useEffect, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import ErrorBoundary from '@components/errors/ErrorBoundary';
import { CategoriesHead, CategoriesListing, ECategoryFile, IListingData, TCategoriesTree, TParsedImageWithType, IArrangementProduct, IVirtualCategoryFilters, INavigationVirtualCategory } from '@features/categories';
import { TCategoryDataFiles, ICategory } from '@features/categories/typescript';
import { IInfoCardWithMedia, TImage } from '@components/typescript';
import MainSeo from '@components/MainSeo/MainSeo';
import { useFiltersStore } from '@features/categories/mobx/stores/FiltersStoreProvider';
import { TCmsBlocks } from '@features/cms/typescript';
import RenderCmsBlock from '@features/cms/components/RenderCmsBlock';
import theme from '@styles/theme';
import { ISeo } from '@typescript/index';
import CategoriesChildren from '@features/categories/components/CategoriesChildren/CategoriesChildren';
import NavigationVirtualCategories from '@features/categories/components/NavigationVirtualCategories/NavigationVirtualCategories';
import ItemListSchema from '@components/ItemListSchema/ItemListSchema';
const DynamicCategoriesFilters = dynamic(() => import('@features/categories/components/CategoriesFilters/CategoriesFilters'), {
  ssr: false
});
type PropsType = {
  categoryId: number;
  categoryName: string;
  categoryFiles: TCategoryDataFiles;
  categoriesTree: TCategoriesTree;
  categoryChildren: Record<symbol, ICategory>;
  listingData: IListingData;
  arrangementProducts: IArrangementProduct[];
  infoCards: IInfoCardWithMedia[];
  categoryBlocks: Record<string, TCmsBlocks> | null;
  seo: ISeo;
  virtualCategoryFilters: IVirtualCategoryFilters;
  navigationVirtualCategories: INavigationVirtualCategory[];
  virtualCategorySlug: string | null;
};
const CategoryListingPage = ({
  categoryId,
  categoryName,
  categoryFiles,
  categoriesTree,
  categoryChildren,
  listingData,
  arrangementProducts,
  infoCards,
  categoryBlocks,
  seo,
  virtualCategoryFilters,
  navigationVirtualCategories,
  virtualCategorySlug
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const mainCategoryImg = useMemo(() => categoryFiles ? categoryFiles.find(file => file.type === ECategoryFile.mainImage) : null, [categoryFiles]);
  const filtersStore = useFiltersStore(listingData, arrangementProducts, t('sort'), categoryId, undefined, virtualCategoryFilters, virtualCategorySlug);
  useEffect(() => {
    if (virtualCategorySlug && virtualCategorySlug !== filtersStore.virtualCategorySlug) {
      filtersStore.clearVirtualCategoryFilters();
      filtersStore.virtualCategoryFilters = virtualCategoryFilters;
      filtersStore.populateStoreWithData(listingData, t('sort'), categoryId);
    }
    if (categoryId !== filtersStore.categoryId) {
      if (filtersStore.previousCategoryId !== categoryId) {
        filtersStore.populateStoreWithData(listingData, t('sort'), categoryId);
      }
      filtersStore.setArrangementProducts(arrangementProducts);
    }
    return () => {
      filtersStore.invalidateStore();
    };
  }, [categoryId, virtualCategorySlug]);
  const router = useRouter();
  const pageValue = router.query.page;

  // Nasłuchuj na zmiany URL i odśwież dane gdy kategoria się zmieni
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      const nextPath = url.split('?')[0];
      const currentPath = router.asPath.split('?')[0];
      if (nextPath === currentPath) return;
      // Nie resetuj paginacji przy wyjściu na kartę produktu (store ma przetrwać browser back)
      if (nextPath.includes('/product/') || currentPath.includes('/product/')) return;
      filtersStore.clearVirtualCategoryFilters();
      filtersStore.fetchAndPopulateStoreWithData(categoryId, t('sort'));
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [categoryId, router, filtersStore, t]);
  const categoryImg: TParsedImageWithType = {
    image: mainCategoryImg?.image as TImage,
    blur: mainCategoryImg?.blur,
    type: mainCategoryImg?.type as ECategoryFile
  };
  const currentPage = pageValue ? parseInt(pageValue as string, 10) : 1;
  const products = listingData?.products || [];
  return <>
      {seo && <MainSeo seo={seo} title={categoryName} />}
      {products.length > 0 && <ItemListSchema products={products} currentPage={currentPage} totalItems={listingData?.pagination?.count} />}
      <CategoriesHead title={categoryName} categoriesTree={categoriesTree} categoryId={categoryId} img={categoryImg} />
      {categoryChildren && <CategoriesChildren categoryChildren={categoryChildren} />}
      <DynamicCategoriesFilters filtersStore={filtersStore} />
      <ErrorBoundary>
        <CategoriesListing filtersStore={filtersStore} categoryId={categoryId} categoryName={categoryName} infoCards={infoCards} />
      </ErrorBoundary>
      {/* {seo.description && <CategoriesSeo content={seo.description} />} */}
      {categoryBlocks && (!pageValue || pageValue === '1') && <div sx={{
      backgroundColor: theme.rawColors.lightGrey300
    }}>
          {Object.keys(categoryBlocks).map(key => <RenderCmsBlock key={key} block={categoryBlocks[key]} defaultBackground={theme.rawColors.lightGrey300} isFirst={false} />)}{' '}
        </div>}
      {navigationVirtualCategories && navigationVirtualCategories.length > 0 && <NavigationVirtualCategories navigationVirtualCategories={navigationVirtualCategories} />}
    </>;
};
export default CategoryListingPage;